import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  User,
  Lock,
  LogOut,
  Eye,
  Search,
  X
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { validateToken } from '../../services/authService';
import { fetchUserData2, useAuth } from '../../context/AuthContext';
import useDebounce from '../../utills/debounce';
import ConfirmationModal from './ConfirmLogout';
import Pagination from './Pagination';
import UserTable from './UserTable';
import CampaignTable from './Campaign_Overview/CampaignTable';
import Campaign_Overview_Section from './Campaign_Overview/campaign_overview';
import { encodeId } from '../../utills/idUtils';

const API_URL = process.env.REACT_APP_API_URL;

const AdminDashboard = () => {
  const { dispatch } = useAuth();
  const [currentTab, setCurrentTab] = useState('Registration Overview');

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loginError, setLoginError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1); // Pagination state
  const [rowsPerPage, setRowsPerPage] = useState(5); // Pagination state
  const [totalPages, setTotalPages] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const navigate = useNavigate();

  // Debounced search term
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Filtered and searched users
  // const filteredUsers = useMemo(() => {
  //   if (!debouncedSearchTerm) return users;

  //   return users.filter(user => {
  //     const searchValue = debouncedSearchTerm?.toLowerCase();
  //     return (
  //       user.first_name?.toLowerCase().includes(searchValue) ||
  //       user.user_name?.toLowerCase().includes(searchValue)
  //     );
  //   });
  // }, [users, debouncedSearchTerm]);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      const isValid = validateToken(token);
      if (isValid) {
        setIsLoggedIn(true);
        fetchUsers(currentPage, rowsPerPage, debouncedSearchTerm);
      }
    }
  }, [currentPage, rowsPerPage, debouncedSearchTerm]);

  useEffect(() => {
    // Reset to page 1 when search term changes to avoid empty pages
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  const handleLogin = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/admin-login`, { email, password });
      localStorage.setItem('adminToken', response.data.token);
      localStorage.setItem('authToken', response.data.token);
      dispatch({ type: 'ADMIN_LOGIN' });

      setIsLoggedIn(true);
      setLoginError('');
      fetchUsers(currentPage, rowsPerPage, debouncedSearchTerm);
    } catch (err) {
      setLoginError('Invalid credentials. Please try again.');
    }
  };

  const fetchUsers = async (page = 1, limit = 10, search = '') => {
    try {
      const response = await axios.get(`${API_URL}/users`,
        {
          params: {
            page,
            limit,
            search
          }
        });
      console.log(response.data, "response.data");
      const { users, totalPages, total } = response.data;
      setUsers(users);
      setTotalPages(totalPages);
      setTotalUsers(total);
    } catch (err) {
      console.error('Failed to fetch users', err);
    }
  };

  const handleSeeProfile = async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/admin-users/${userId}`);
      const { user, adminUsrtoken } = response.data;

      localStorage.setItem('adminUsrtoken', adminUsrtoken);
      dispatch({ type: 'SET_ADMIN_TOKEN', payload: { adminToken: adminUsrtoken } });
      // dispatch({ type: 'LOGIN', payload: { token: adminUsrtoken } });
      await fetchUserData2(adminUsrtoken, dispatch)
      navigate(`/assessment`);
      // window.location.reload();
      setSelectedUser(user);
      setIsModalOpen(true);
    } catch (err) {
      console.error('Failed to fetch user profile', err);
    }
  };


  const handleSeeCampaigns = async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/admin-users/${userId}`);
      const { adminUsrtoken } = response.data;

      localStorage.setItem('adminUsrtoken', adminUsrtoken);
      dispatch({ type: 'SET_ADMIN_TOKEN', payload: { adminToken: adminUsrtoken } });

      const encodedId = encodeId(userId);
      navigate(`/user/${encodedId}/campaigns`);
      // window.location.reload();
    } catch (err) {
      console.error('Failed to fetch user campaigns or admin token', err);
    }
  };



  const handleLogout = () => {
    setIsModalOpen(true);
  };

  const confirmLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUsrtoken');
    localStorage.removeItem('authToken');
    setIsLoggedIn(false);
    window.location.href = '/admin-dashboard';
  };

  const cancelLogout = () => {
    setIsModalOpen(false);
  };

  // Login Form Component
  const LoginForm = () => (
    <div className="min-h-screen flex items-center justify-center bg-blue-50">
      <div className="w-full max-w-md bg-white shadow-md rounded-lg p-8">
        <h2 className="text-center text-2xl font-bold text-blue-600 mb-6">
          Admin Login
        </h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const email = e.target.email.value;
            const password = e.target.password.value;
            handleLogin(email, password);
          }}
          className="space-y-4"
        >
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500" />
            <input
              type="email"
              name="email"
              placeholder="Email"
              required
              className="w-full pl-10 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500" />
            <input
              type="password"
              name="password"
              placeholder="Password"
              required
              className="w-full pl-10 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {loginError && (
            <p className="text-red-500 text-sm text-center">{loginError}</p>
          )}
          <button
            type="submit"
            className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 transition duration-300"
          >
            Login
          </button>
        </form>
      </div>
    </div>
  );

  // User Profile Modal
  const UserProfileModal = () => (
    isModalOpen && selectedUser && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-8 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl text-blue-600">User Profile</h2>
            <button
              onClick={() => setIsModalOpen(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto">
            {JSON.stringify(selectedUser, null, 2)}
          </pre>
        </div>
      </div>
    )
  );

  // If not logged in, show login form
  if (!isLoggedIn) return <LoginForm />;

  return (
    <div className="min-h-screen bg-blue-50 p-4 md:p-8">
      <div className="bg-white shadow-md rounded-lg">
        <div className="flex flex-col md:flex-row items-center justify-between p-6 border-b border-blue-100">
          <h2 className="text-2xl font-bold text-blue-600 mb-4 md:mb-0">
            Admin Dashboard
          </h2>
          <button
            onClick={handleLogout}
            className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 transition duration-300 flex items-center"
          >
            <LogOut className="mr-2" /> Logout
          </button>
          <ConfirmationModal
            isOpen={isModalOpen}
            onConfirm={confirmLogout}
            onCancel={cancelLogout}
          />
        </div>
        <div className="p-6">

          {/* Tabs */}
          <div className="flex space-x-4 mb-6 border-b border-blue-200">
            {['Registration Overview', 'Campaign Overview'].map((tab) => (
              <button
                key={tab}
                onClick={() => setCurrentTab(tab)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-all duration-300 ${currentTab === tab
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-blue-500'
                  }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {currentTab === 'Registration Overview' && (<>
            <div className="relative lg:w-[50%] w-full mb-4">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500">
                <Search className="h-5 w-5" />
              </div>
              <input
                placeholder="Search users by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <UserTable
              users={users}
              onSeeProfile={handleSeeProfile}
              onSeeCampaigns={handleSeeCampaigns}
            />
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                rowsPerPage={rowsPerPage}
                onPageChange={(page) => setCurrentPage(page)}
                onRowsPerPageChange={(rows) => {
                  setRowsPerPage(rows);
                  setCurrentPage(1); // Reset to page 1 when rows per page changes
                }}
              />
            )}
          </>)}

          {currentTab === 'Campaign Overview' && (
            <Campaign_Overview_Section />
          )}
        </div>
      </div>

      <UserProfileModal />
    </div>
  );
};

export default AdminDashboard;